<template>
  <div class="bar-chart-card" :class="{ 'bar-chart-card--dark': isDark }">

    <div class="bar-chart-header">
      <div>
        <div class="bar-chart-title">Trafik Tiket Bulan Ini</div>
        <div class="bar-chart-subtitle">{{ currentMonthName }}</div>
      </div>
      <div class="bar-chart-badge" :class="{ 'bar-chart-badge--dark': isDark }">{{ totalThisMonth }} tiket</div>
    </div>

    <div class="bar-chart-stats" :class="{ 'bar-chart-stats--dark': isDark }">
      <div class="bar-chart-stat">
        <span class="bar-chart-stat__value">{{ totalThisMonth }}</span>
        <span class="bar-chart-stat__label">Total</span>
      </div>
      <div class="bar-chart-stat-divider"></div>
      <div class="bar-chart-stat">
        <span class="bar-chart-stat__value">{{ avgPerDay }}</span>
        <span class="bar-chart-stat__label">Rata-rata/hari</span>
      </div>
      <div class="bar-chart-stat-divider"></div>
      <div class="bar-chart-stat">
        <span class="bar-chart-stat__value">{{ peakCount }}</span>
        <span class="bar-chart-stat__label">Puncak (tgl {{ peakDay }})</span>
      </div>
      <div class="bar-chart-stat-divider"></div>
      <div class="bar-chart-stat">
        <span class="bar-chart-stat__value">{{ activeDays }}</span>
        <span class="bar-chart-stat__label">Hari aktif</span>
      </div>
    </div>

    <div class="bar-chart-canvas-wrap">
      <Bar v-if="ready" :key="isDark ? 'dark' : 'light'" :data="chartData" :options="chartOptions" :plugins="[zoomFixPlugin]" />
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { Bar } from 'vue-chartjs'
import {
  Chart as ChartJS,
  Title, Tooltip, Legend,
  BarElement, CategoryScale, LinearScale,
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, ChartDataLabels)

const zoomFixPlugin = {
  id: 'zoomFix',
  beforeEvent(chart, args) {
    if (!args.event?.native) return
    const native = args.event.native
    const rect = chart.canvas.getBoundingClientRect()
    if (!rect.width) return
    const scaleX = chart.canvas.offsetWidth / rect.width
    const scaleY = chart.canvas.offsetHeight / rect.height
    args.event.x = (native.clientX - rect.left) * scaleX
    args.event.y = (native.clientY - rect.top) * scaleY
  }
}

const props = defineProps({
  data: { type: Array, required: true },
})

const ready = ref(false)
const isDark = ref(false)
let observer = null

function detectDark() {
  isDark.value = document.querySelector('.layout-app')?.classList.contains('dark') ?? false
}

onMounted(() => {
  detectDark()
  const layoutEl = document.querySelector('.layout-app')
  if (layoutEl) {
    observer = new MutationObserver(detectDark)
    observer.observe(layoutEl, { attributes: true, attributeFilter: ['class'] })
  }
  ready.value = true
})

onBeforeUnmount(() => {
  if (observer) observer.disconnect()
})

const currentMonthName = computed(() =>
  new Date().toLocaleDateString('id-ID', { month: 'long', year: 'numeric' })
)

const totalThisMonth = computed(() => props.data.reduce((s, d) => s + d.count, 0))
const avgPerDay = computed(() => {
  const active = props.data.filter(d => d.count > 0)
  return active.length ? Math.round(totalThisMonth.value / active.length) : 0
})
const peakItem = computed(() => props.data.reduce((max, d) => d.count > max.count ? d : max, { count: 0, day: '-' }))
const peakCount = computed(() => peakItem.value.count)
const peakDay = computed(() => peakItem.value.day)
const activeDays = computed(() => props.data.filter(d => d.count > 0).length)

const chartData = computed(() => ({
  labels: props.data.map(d => d.day.toString()),
  datasets: [{
    label: 'Tiket',
    data: props.data.map(d => d.count),
    backgroundColor: (ctx) => {
      const { ctx: c, chartArea } = ctx.chart
      if (!chartArea) return '#166534'
      const gradient = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
      gradient.addColorStop(0, '#16a34a')
      gradient.addColorStop(1, '#14532d')
      return gradient
    },
    borderRadius: 8,
    borderSkipped: false,
    datalabels: {
      anchor: 'end',
      align: 'end',
      color: (ctx) => {
        if (ctx.dataset.data[ctx.dataIndex] === peakCount.value && peakCount.value > 0)
          return isDark.value ? '#4ade80' : '#14532d'
        return isDark.value ? '#888888' : '#94a3b8'
      },
      font: { weight: '700', size: 10 },
      formatter: (v) => v > 0 ? v : '',
    },
  }],
}))

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 600, easing: 'easeInOutQuart' },
  plugins: {
    legend: { display: false },
    datalabels: { display: true },
    tooltip: {
      backgroundColor: isDark.value ? '#222222' : '#14532d',
      titleColor: isDark.value ? '#888888' : '#86efac',
      bodyColor: '#ffffff',
      padding: 10,
      cornerRadius: 8,
      displayColors: false,
      callbacks: {
        title: (items) => `Tanggal ${items[0].label}`,
        label: (ctx) => `${ctx.parsed.y} tiket masuk`,
      },
    },
  },
  scales: {
    x: {
      grid: { display: false },
      border: { display: false },
      ticks: {
        color: isDark.value ? '#888888' : '#94a3b8',
        font: { size: 11 },
        maxRotation: 0,
      },
    },
    y: {
      beginAtZero: true,
      border: { display: false, dash: [4, 4] },
      grid: { color: isDark.value ? '#2a2a2a' : '#f1f5f9', lineWidth: 1 },
      ticks: {
        precision: 0,
        stepSize: 1,
        color: isDark.value ? '#888888' : '#94a3b8',
        font: { size: 11 },
      },
    },
  },
}))
</script>

<style scoped>
.bar-chart-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.05);
  width: 100%;
  transition: background 0.2s, border-color 0.2s;
}

.bar-chart-card--dark {
  background: #1a1a1a;
  border-color: #2a2a2a;
  box-shadow: 0 4px 20px rgba(0,0,0,0.3);
}

.bar-chart-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
}

.bar-chart-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.3;
}

.bar-chart-card--dark .bar-chart-title { color: #f1f5f9; }

.bar-chart-subtitle {
  font-size: 13px;
  color: #94a3b8;
  margin-top: 2px;
  text-transform: capitalize;
}

.bar-chart-card--dark .bar-chart-subtitle { color: #555555; }

.bar-chart-badge {
  background: #f0fdf4;
  color: #14532d;
  border: 1px solid #86efac;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 999px;
  white-space: nowrap;
}

.bar-chart-badge--dark {
  background: rgba(20,83,45,0.25);
  color: #4ade80;
  border-color: rgba(74,222,128,0.2);
}

.bar-chart-stats {
  display: flex;
  align-items: center;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 12px;
  padding: 12px 20px;
  margin-bottom: 24px;
}

.bar-chart-stats--dark {
  background: #1a2e1a;
  border-color: #14532d;
}

.bar-chart-stat {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.bar-chart-stat__value {
  font-size: 20px;
  font-weight: 800;
  color: #14532d;
  line-height: 1;
}

.bar-chart-card--dark .bar-chart-stat__value { color: #4ade80; }

.bar-chart-stat__label {
  font-size: 11px;
  color: #166534;
  font-weight: 500;
  text-align: center;
}

.bar-chart-card--dark .bar-chart-stat__label { color: #555555; }

.bar-chart-stat-divider {
  width: 1px;
  height: 36px;
  background: #86efac;
  flex-shrink: 0;
}

.bar-chart-card--dark .bar-chart-stat-divider { background: #14532d; }

.bar-chart-canvas-wrap {
  position: relative;
  width: 100%;
  height: 300px;
}

@media (max-width: 640px) {
  .bar-chart-stats {
    flex-wrap: wrap;
    gap: 12px;
    padding: 12px;
  }
  .bar-chart-stat { min-width: calc(50% - 6px); }
  .bar-chart-stat-divider { display: none; }
  .bar-chart-canvas-wrap { height: 220px; }
}
</style>
